import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  getMyBrandingWithCache,
  clearCachedBranding,
  clearCachedMyBranding,
  getBrandingBySubdomain,
} from '../api/brandingAPI';
import { BrandingPreviewDto, DEFAULT_BRANDING, getBrandingCssVars } from '../models/BrandingDTO';

import { ActivePracticeContext } from './ActivePracticeContext';
import { useAuth } from './AuthContext';

/**
 * BrandingContext provides practice branding (logo, colors) throughout the app.
 * It handles:
 * - Fetching branding on login
 * - Caching branding for efficiency
 * - Providing CSS variables for theming
 * - Fallback to default HealioSpace branding
 */

interface BrandingContextValue {
  /** Current branding configuration */
  branding: BrandingPreviewDto;

  /** Whether branding has been loaded */
  isLoaded: boolean;

  /** Whether branding is currently being fetched */
  isLoading: boolean;

  /** Force refresh branding from API */
  refresh: () => Promise<void>;

  /** Clear cached branding (call on logout) */
  clearBranding: () => void;

  /** CSS variables derived from branding */
  cssVars: Record<string, string>;

  /** Whether practice has custom branding configured */
  hasCustomBranding: boolean;
}

const defaultValue: BrandingContextValue = {
  branding: DEFAULT_BRANDING,
  isLoaded: false,
  isLoading: false,
  refresh: async () => undefined,
  clearBranding: () => undefined,
  cssVars: getBrandingCssVars(DEFAULT_BRANDING),
  hasCustomBranding: false,
};

const BrandingContext = createContext<BrandingContextValue>(defaultValue);

interface BrandingProviderProps {
  children: ReactNode;
  /**
   * Optional subdomain for unauthenticated branding lookup.
   * Used on login page when accessing via {practice}.healiospace.com
   */
  subdomain?: string;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children, subdomain }) => {
  const { isAuthenticated } = useAuth();
  // HA-320: read the active-practice selection defensively. BrandingProvider
  // is also rendered standalone (SignUp page, tests) outside
  // ActivePracticeProvider, so useContext (not the throwing useActivePractice
  // hook) is used — currentPracticeId stays null when there is no provider.
  const activePractice = useContext(ActivePracticeContext);
  const currentPracticeId = activePractice?.currentPracticeId ?? null;
  const [branding, setBranding] = useState<BrandingPreviewDto>(DEFAULT_BRANDING);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBranding = useCallback(
    async (forceRefresh = false) => {
      setIsLoading(true);

      try {
        let fetchedBranding: BrandingPreviewDto | null = null;

        if (subdomain) {
          // Unauthenticated: fetch by subdomain (for login page).
          fetchedBranding = await getBrandingBySubdomain(subdomain);
        } else {
          // HA-267: Authenticated path uses /api/v1/branding/me. The
          // backend resolves the caller's practice from the token and
          // returns either custom practice branding (license-gated) or
          // default Healio branding — always 200, never 400. Clients
          // are no longer skipped; the legacy Client-skip introduced
          // in commit 2d31a00c is gone.
          fetchedBranding = await getMyBrandingWithCache(forceRefresh);
        }

        if (fetchedBranding) {
          setBranding(fetchedBranding);
        } else {
          setBranding(DEFAULT_BRANDING);
        }
      } catch (error) {
        console.error('Error fetching branding:', error);
        setBranding(DEFAULT_BRANDING);
      } finally {
        setIsLoaded(true);
        setIsLoading(false);
      }
    },
    [subdomain]
  );

  const refresh = useCallback(async () => {
    await fetchBranding(true);
  }, [fetchBranding]);

  const clearBranding = useCallback(() => {
    clearCachedBranding();
    clearCachedMyBranding();
    setBranding(DEFAULT_BRANDING);
    setIsLoaded(false);
  }, []);

  // HA-288: Skip the authenticated /branding/me call on public pages
  // when there's no subdomain hint and no logged-in user. Without this
  // gate, every public route fires a 401 that the global axios
  // interceptor converts into a session_expired redirect to SignUp.
  // Re-runs on auth-state transitions so post-login branding still hydrates.
  useEffect(() => {
    if (!subdomain && !isAuthenticated) {
      setBranding(DEFAULT_BRANDING);
      setIsLoaded(true);

      return;
    }
    fetchBranding();
  }, [fetchBranding, subdomain, isAuthenticated]);

  // HA-457: sign-in and sign-out in another tab reload this one (api/sessionAPI.ts), which reads
  // branding afresh; the cached copy is cleared on sign-in and sign-out (clearUserData).

  // HA-320: when the patient switches practice, the cached branding belongs
  // to the previous practice. Clear it and force a re-fetch — the axios
  // interceptor appends ?practice_id= so the new practice's branding is
  // returned. The initial resolution (undefined -> first value) is skipped;
  // the auth-state effect above already performs the first fetch.
  const prevPracticeIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (prevPracticeIdRef.current === undefined) {
      prevPracticeIdRef.current = currentPracticeId;

      return;
    }
    if (prevPracticeIdRef.current === currentPracticeId) {
      return;
    }
    prevPracticeIdRef.current = currentPracticeId;
    clearCachedMyBranding();
    fetchBranding(true);
  }, [currentPracticeId, fetchBranding]);

  const cssVars = useMemo(() => getBrandingCssVars(branding), [branding]);

  const hasCustomBranding = useMemo(() => {
    return (
      branding.logoUrl !== null ||
      branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
      branding.secondaryColor !== DEFAULT_BRANDING.secondaryColor ||
      branding.displayName !== DEFAULT_BRANDING.displayName
    );
  }, [branding]);

  const contextValue = useMemo(
    () => ({
      branding,
      isLoaded,
      isLoading,
      refresh,
      clearBranding,
      cssVars,
      hasCustomBranding,
    }),
    [branding, isLoaded, isLoading, refresh, clearBranding, cssVars, hasCustomBranding]
  );

  return <BrandingContext.Provider value={contextValue}>{children}</BrandingContext.Provider>;
};

/**
 * Hook to access practice branding context.
 *
 * @example
 * ```tsx
 * const { branding, cssVars, hasCustomBranding } = usePracticeBranding();
 *
 * // Use logo
 * <img src={branding.logoUrl || defaultLogo} alt={branding.displayName} />
 *
 * // Apply CSS variables
 * <div style={cssVars}>
 *   <button style={{ backgroundColor: 'var(--branding-primary-color)' }}>
 *     Click me
 *   </button>
 * </div>
 * ```
 */
export const usePracticeBranding = (): BrandingContextValue => {
  return useContext(BrandingContext);
};

/**
 * Hook to get just the branding colors for styling.
 */
export const useBrandingColors = () => {
  const { branding } = usePracticeBranding();

  return {
    primary: branding.primaryColor,
    secondary: branding.secondaryColor,
    accent: branding.accentColor,
    background: branding.backgroundColor,
    text: branding.textColor,
  };
};

/**
 * Hook to get the practice display name and logo.
 */
export const useBrandingIdentity = () => {
  const { branding, hasCustomBranding } = usePracticeBranding();

  return {
    displayName: branding.displayName || 'HealioSpace',
    logoUrl: branding.logoUrl,
    tagline: branding.tagline,
    showPoweredBy: branding.showPoweredBy,
    hasCustomBranding,
  };
};

export default BrandingContext;
